import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

import { normalizeIndent } from "./formattingConfig.mjs";

const INDENTED_NODE_TYPES = Object.freeze(["paragraph", "heading", "blockquote"]);
const INDENTED_NODE_TYPE_SET = new Set(INDENTED_NODE_TYPES);
const INDENT_NOOP_CANDIDATE = new PluginKey("indentNoopCandidate");

export const Indent = Extension.create({
  name: "indent",

  addGlobalAttributes() {
    return [{
      types: [...INDENTED_NODE_TYPES],
      attributes: {
        indent: {
          default: 0,
          parseHTML: parseIndent,
          renderHTML: ({ indent }) => renderIndent(indent)
        }
      }
    }];
  },

  addCommands() {
    return {
      setIndent: (level) => (props) => runIndentCommand(props, () => normalizeIndent(level)),
      increaseIndent: () => (props) => runIndentCommand(props, (indent) => normalizeIndent(indent + 1)),
      decreaseIndent: () => (props) => runIndentCommand(props, (indent) => normalizeIndent(indent - 1))
    };
  },

  addProseMirrorPlugins() {
    return [new Plugin({
      key: INDENT_NOOP_CANDIDATE,
      filterTransaction: (transaction) => !isEmptyIndentCandidate(transaction)
    })];
  }
});

function parseIndent(element) {
  try {
    const attributeValue = element.getAttribute("data-indent");
    if (attributeValue !== null) return normalizeIndent(Number(attributeValue));
    const margin = element.style?.marginLeft;
    const match = typeof margin === "string" ? margin.trim().match(/^(\d+(?:\.\d+)?)em$/i) : null;
    return match ? normalizeIndent(Number(match[1]) / 2) : 0;
  } catch {
    return 0;
  }
}

function renderIndent(value) {
  const indent = normalizeIndent(value);
  if (indent === 0) return {};
  return { "data-indent": String(indent), style: `margin-left:${indent * 2}em` };
}

function runIndentCommand({ tr, state, dispatch }, transform) {
  const changed = updateIndentInSelection(tr, state.selection, transform, Boolean(dispatch));
  if (!changed && dispatch) tr.setMeta(INDENT_NOOP_CANDIDATE, true);
  return true;
}

function transactionHasEffects(transaction) {
  return transaction.steps.length > 0
    || transaction.selectionSet
    || transaction.storedMarksSet
    || transaction.scrolledIntoView;
}

function isEmptyIndentCandidate(transaction) {
  if (transaction.getMeta(INDENT_NOOP_CANDIDATE) !== true || transactionHasEffects(transaction)) return false;
  return Object.keys(transaction.meta).every((key) => key === INDENT_NOOP_CANDIDATE.key);
}

function updateIndentInSelection(transaction, selection, transform, apply) {
  let changed = false;
  transaction.doc.nodesBetween(selection.from, selection.to, (node, position) => {
    if (!INDENTED_NODE_TYPE_SET.has(node.type.name)) return;
    const current = normalizeIndent(node.attrs.indent);
    const indent = normalizeIndent(transform(current));
    if (indent === current) return;
    changed = true;
    if (apply) transaction.setNodeMarkup(position, undefined, { ...node.attrs, indent });
  });
  return changed;
}
